import {
  buildHierarchyTree,
  getAllContributorsTeam,
  isGroup,
  traverseAndOrder
} from 'common/utils/utils';
import _ from 'lodash';
import { TEAM_TYPES } from 'common/constants/constants';
import { createSelector } from 'reselect';
import { myAccountSelector } from 'state/selectors/account';
import { settingsSelector } from 'state/selectors/settings';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { SYNCED_TEAM_MODEL } from 'views/root/views/home/views/settings/views/ContributorsAndTeams/Teams/teams.model';
import { flattenTree } from 'components/base/MultiLevelDropdown/MultiLevelDropdown.utils';

export const isTeamSynced = team => !!_.get(team, 'extra.setting_key');

const appendExtraSyncDataToTeam = team => ({
  ...team,
  ...(isTeamSynced(team)
    ? {
        [SYNCED_TEAM_MODEL.IS_SYNCED]: true,
        [SYNCED_TEAM_MODEL.SYNCED_PROVIDER_NAME]: _.get(
          team,
          'extra.provider_team_name'
        )
      }
    : {
        [SYNCED_TEAM_MODEL.IS_SYNCED]: false
      })
});

export const teamsSelector = state => _.get(state, 'currentTeams', []);

export const teamsByIdSelector = createSelector([teamsSelector], teams =>
  _.keyBy(teams, 'id')
);

// TODO: change name to teamsWithExtraDataSelector
export const teamsWithExtraSyncedDataSelector = createSelector(
  [teamsSelector],
  teams => {
    const syncedTeamsParentIds = _.keyBy(
      teams
        .filter(({ type }) => type === TEAM_TYPES.SYNCED)
        .map(({ parent_id }) => parent_id)
    );

    return teams.map(team => ({
      ...appendExtraSyncDataToTeam(team),
      isParent: checkIfParent(team, syncedTeamsParentIds)
    }));
  }
);

/**
 * Determines if the given team is considered a parent based on its type and associated parent IDs.
 * @param {object} team - The team object to ckheck.
 * @param {string} team.id - The ID of the team.
 * @param {string} team.type - The type of the team.
 * @param {object} syncedTeamsParentIds - An object containing IDs of synced team parents.
 * @returns {boolean} - Returns `true` if the team is considered a parent, otherwise `false`.
 */
const checkIfParent = (team, syncedTeamsParentIds) => {
  switch (team.type) {
    case TEAM_TYPES.SYNCED:
      return syncedTeamsParentIds[team.id] ? true : false;
    case TEAM_TYPES.ALL_TEAMS:
    case TEAM_TYPES.GROUP:
      return true;
    case TEAM_TYPES.STANDARD:
    case TEAM_TYPES.ALL_CONTRIBUTORS:
    default:
      return false;
  }
};

export const noParentTeamsSelector = createSelector(
  teamsWithExtraSyncedDataSelector,
  teams => teams.filter(({ isParent }) => !isParent)
);
export const syncTeamsTreeSelector = createSelector(
  [teamsWithExtraSyncedDataSelector],
  teams => buildHierarchyTree({ data: teams })
);

export const teamsFlatTreeSelector = createSelector(
  [syncTeamsTreeSelector],
  teams => flattenTree(teams, 'children')
);

export const syncTeamsOnlySelector = createSelector(
  [teamsWithExtraSyncedDataSelector],
  teams => teams.filter(team => team[SYNCED_TEAM_MODEL.IS_SYNCED])
);

export const allContributorsTeamSelector = createSelector(
  [teamsWithExtraSyncedDataSelector],
  teams => getAllContributorsTeam(teams)
);

export const syncTeamsRootLevelTreeSelector = createSelector(
  [
    syncTeamsTreeSelector,
    teamsFlatTreeSelector,
    myAccountSelector,
    settingsSelector
  ],
  (teamsTree, teams, account, settings) => {
    const rootTeamId = _.get(account, 'settings.root_team');
    const rootTeamExists = teams.find(({ id }) => id === rootTeamId);
    const filterByRootTeamId = rootTeamId && rootTeamExists;

    const treeTeams = filterByRootTeamId
      ? [teams.find(({ id }) => id === rootTeamId)].filter(Boolean)
      : teamsTree;

    return treeTeams || [];
  }
);

export const selectedTeamWithExtraDataSelector = createSelector(
  [teamsFlatTreeSelector, selectedTeamSelector],
  (teams, selectedTeam) => {
    const selectedTeamId = _.get(selectedTeam, 'id');

    const treeTeams = selectedTeamId
      ? [teams.find(({ id }) => id === selectedTeamId)].filter(Boolean)
      : [];

    return treeTeams || [];
  }
);

// Returns the selected group ordered
export const selectedGroupWithExtraDataOrderedSelector = createSelector(
  [selectedTeamWithExtraDataSelector],
  selectedTeamWithExtraData => {
    let orderedSelectedGroup = {};

    if (
      !!selectedTeamWithExtraData.length &&
      isGroup(selectedTeamWithExtraData[0])
    ) {
      orderedSelectedGroup = traverseAndOrder(selectedTeamWithExtraData[0], []);
    }

    return orderedSelectedGroup;
  }
);

export const isSelectedTeamParentSelector = createSelector(
  [selectedTeamWithExtraDataSelector],
  selectedTeamWithExtraData => {
    const { isParent, type } = selectedTeamWithExtraData?.[0] || {};
    const isGroup = isParent || type === TEAM_TYPES.GROUP;

    return isGroup;
  }
);

export const getRootTeamSelector = createSelector(
  [myAccountSelector, teamsFlatTreeSelector],
  (account, teams) => {
    const rootTeamId = _.get(account, 'settings.root_team');
    const rootTeamExists = teams.find(({ id }) => id === rootTeamId);

    return rootTeamExists ? rootTeamId : undefined;
  }
);
